// Recovering from stale asset chunks after a deploy. Vite fingerprints every lazily
// imported route/component into a hashed filename, so a new deploy rotates those
// names. A tab left open before the deploy then asks for chunk files that no longer
// exist and the dynamic import() rejects — surfacing as a rejected navigation
// (router.onError) or an uncaught render error (app.config.errorHandler). Rather than
// dying on a dead link or a blank screen, we force a single full-page reload so the
// browser fetches the new index.html and the fresh chunk names.

// Message shapes browsers use when a dynamic import() can't be fetched. Covers
// Chromium, Firefox and WebKit wording plus the generic ChunkLoadError name.
const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /chunkloaderror/i,
];

export function isStaleChunkError(error) {
  const message = error?.message ?? String(error ?? "");
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

// One reload, and only one. The flag lives in sessionStorage so it survives the reload
// but not the tab: if the fresh build still can't load the chunk (a genuinely broken
// deploy) we let the real error surface the second time instead of trapping the user
// in a refresh loop. Returns whether a reload was triggered.
const RELOAD_FLAG = "pandig:stale-chunk-reloaded";

export function reloadForStaleChunk(target) {
  if (sessionStorage.getItem(RELOAD_FLAG)) return false;
  sessionStorage.setItem(RELOAD_FLAG, "1");
  if (target) {
    window.location.assign(target);
  } else {
    window.location.reload();
  }
  return true;
}

// Cleared once we navigate successfully again, so a later, unrelated stale-chunk event
// (e.g. a second deploy while the tab is still open) can trigger its own reload.
export function clearStaleChunkFlag() {
  sessionStorage.removeItem(RELOAD_FLAG);
}
